import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { VoiceButton } from "@/components/VoiceButton";
import { AiAvatar } from "@/components/AiAvatar";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AiChatPanel } from "@/components/editor/AiChatPanel";
import { Design3dModal } from "@/components/editor/Design3dModal";
import { TYPE_COLOR, FONT_CLASS_CSS, SEND_FORMATS, HANDLES, FILTER_DEFAULTS, cssFilter, rgbToHex }
  from "@/lib/editorUtils";
import { Eye, EyeOff, Trash2, Save, Download, Layers, Loader2, Coins, ArrowLeft, Wand2, Send, Bold, Italic, Type, UploadCloud, X, MousePointer2, Pipette, Brush, Eraser, Square, PaintBucket, Undo2, Redo2, Sparkles, ArrowUp, ArrowDown, Copy, Lock, LockOpen, Languages, Highlighter, MessageSquare, Bot, RotateCcw, CornerDownLeft, Crop, RotateCw, Maximize2, FileStack, ChevronLeft, ChevronRight, FileText, Lasso, Box, SlidersHorizontal, Sun, Minimize2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Cursor del lienzo según la herramienta activa. */
const canvasCursor = (tool) => {
  if (tool === "select") return "default";
  if (tool === "text") return "text";
  return "crosshair";
};

/** Color del rectángulo de previsualización según la herramienta. */
const previewStroke = (tool) => {
  if (tool === "marker") return "2px dashed #F59E0B";
  if (tool === "crop") return "2px dashed #FF9440";
  return "1px dashed #C2620E";
};

const previewFill = (tool, color) => {
  if (tool === "marker") return "rgba(245,158,11,0.25)";
  if (tool === "crop") return "rgba(34,211,238,0.15)";
  return `${color}55`;
};

export default function Editor() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const { user: adminUser } = useAuth();

  const [project, setProject] = useState(null);
  const [layers, setLayers] = useState([]);
  // el panel lista de arriba abajo y el lienzo pinta de abajo hacia arriba
  const layersTopDown = useMemo(
    () => [...layers].sort((a, b) => (b.order || 0) - (a.order || 0)), [layers]);
  const layersBottomUp = useMemo(
    () => [...layers].sort((a, b) => (a.order || 0) - (b.order || 0)), [layers]);

  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cost, setCost] = useState(50);
  const [downloads, setDownloads] = useState(null);
  const [dewarping, setDewarping] = useState(false);
  const [dewarpPreview, setDewarpPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [fontUploading, setFontUploading] = useState(false);
  const [library, setLibrary] = useState([]);
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiProvider, setAiProvider] = useState("gemini");
  const [sendFormat, setSendFormat] = useState("zip");
  const [rev, setRev] = useState(0);
  const [tool, setTool] = useState("select");
  const [activeColor, setActiveColor] = useState("#111111");
  const [brushSize, setBrushSize] = useState(16);
  const [bgReady, setBgReady] = useState(false);
  const [histTick, setHistTick] = useState(0);
  const [rectPreview, setRectPreview] = useState(null);
  const [markerRegion, setMarkerRegion] = useState(null);
  const [aiCmd, setAiCmd] = useState("");
  const [aiCmdBusy, setAiCmdBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [pages, setPages] = useState([]);
  const [cropRegion, setCropRegion] = useState(null);
  const [cropBusy, setCropBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pagesBusy, setPagesBusy] = useState(false);
  const [bgFilters, setBgFilters] = useState({ brightness: 100, contrast: 100, saturation: 100 });
  const [lassoPts, setLassoPts] = useState(null);
  const [lassoPath, setLassoPath] = useState(null);
  const [show3d, setShow3d] = useState(false);
  const [obj3d, setObj3d] = useState("");
  const [mode3d, setMode3d] = useState("document");
  const [quality, setQuality] = useState("2k");
  const lassoRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const rotateRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const paintingRef = useRef(false);
  const lastPtRef = useRef(null);
  const rectStartRef = useRef(null);
  const eraserColorRef = useRef("#ffffff");
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const aiLayerUndoRef = useRef(null);
  const chatEndRef = useRef(null);

  const authOpts = { withCredentials: true };
  const q = token ? `?token=${token}` : "";
  const fileToken = useRef("");

  const fileUrl = (name) =>
    `${BACKEND_URL}/api/editor/${jobId}/file/${name}` +
    (fileToken.current ? `?token=${fileToken.current}&v=${rev}` : `?v=${rev}`);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/editor/${jobId}${q}`, authOpts);
        setProject(data);
        setLayers(data.layers.map((l) => ({ ...l })));
        setCost(data.editor_cost);
        fileToken.current = data.file_token || token;
        loadLibrary();
        loadPages();
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo cargar el proyecto");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [jobId]);

  const scale = project ? Math.min(900, project.canvas.width) / project.canvas.width : 1;
  const dispW = project ? project.canvas.width * scale : 0;
  const dispH = project ? project.canvas.height * scale : 0;

  const updateLayer = (id, patch) =>
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const updateStyle = (id, patch) =>
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, style: { ...(l.style || {}), ...patch } } : l)));

  const onPointerDown = (e, layer) => {
    if (layer.type === "BACKGROUND") return;
    e.stopPropagation();
    setSelected(layer.id);
    if (layer.locked) return;
    dragRef.current = { id: layer.id, startX: e.clientX, startY: e.clientY, bbox: [...layer.bbox] };
  };

  const bringForward = (id) => setLayers((ls) => {
    const max = ls.reduce((m, l) => Math.max(m, l.order || 0), 0);
    return ls.map((l) => (l.id === id ? { ...l, order: max + 1 } : l));
  });
  const sendBackward = (id) => setLayers((ls) => {
    const min = ls.reduce((m, l) => Math.min(m, l.order || 0), 0);
    return ls.map((l) => (l.id === id ? { ...l, order: min - 1 } : l));
  });
  const duplicateLayer = (id) => setLayers((ls) => {
    const src = ls.find((l) => l.id === id);
    if (!src) return ls;
    const max = ls.reduce((m, l) => Math.max(m, l.order || 0), 0);
    const [x, y, w, h] = src.bbox;
    const copy = { ...src, id: `${src.type.toLowerCase()}_${Date.now()}`, name: `${src.name || src.type} copia`,
      order: max + 1, bbox: [x + 15, y + 15, w, h], style: { ...(src.style || {}) } };
    return [...ls, copy];
  });
  const toggleLock = (id) => setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, locked: !l.locked } : l)));

  const startResize = (e, layer, dir) => {
    e.stopPropagation();
    e.preventDefault();
    resizeRef.current = {
      id: layer.id, dir, type: layer.type, startX: e.clientX, startY: e.clientY,
      bbox: [...layer.bbox], fs: (layer.style || {}).font_size_px || layer.bbox[3],
    };
  };

  const startRotate = (e, layer) => {
    e.stopPropagation();
    e.preventDefault();
    const [x, y, w, h] = layer.bbox;
    rotateRef.current = { id: layer.id, cx: x + w / 2, cy: y + h / 2 };
  };

  const resetTransform = () => {
    if (!selected) return;
    updateStyle(selected, { rotation: 0 });
    toast.success("Rotación restablecida.");
  };

  const onPointerMove = useCallback((e) => {
    const rz = resizeRef.current;
    if (rz) {
      const dx = (e.clientX - rz.startX) / scale;
      const dy = (e.clientY - rz.startY) / scale;
      const [x0, y0, w0, h0] = rz.bbox;
      let x = x0, y = y0, w = w0, h = h0;
      if (rz.dir.includes("e")) w = Math.max(8, w0 + dx);
      if (rz.dir.includes("s")) h = Math.max(8, h0 + dy);
      if (rz.dir.includes("w")) { w = Math.max(8, w0 - dx); x = x0 + (w0 - w); }
      if (rz.dir.includes("n")) { h = Math.max(8, h0 - dy); y = y0 + (h0 - h); }
      setLayers((ls) => ls.map((l) => {
        if (l.id !== rz.id) return l;
        const nl = { ...l, bbox: [Math.round(x), Math.round(y), Math.round(w), Math.round(h)] };
        if (rz.type === "TEXT" && h0 > 0) {
          nl.style = { ...(l.style || {}), font_size_px: Math.max(6, Math.round(rz.fs * (h / h0))) };
        }
        return nl;
      }));
      return;
    }
    const rt = rotateRef.current;
    if (rt) {
      const box = canvasRef.current?.getBoundingClientRect();
      if (!box) return;
      const cx = box.left + rt.cx * scale;
      const cy = box.top + rt.cy * scale;
      let ang = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90;
      if (e.shiftKey) ang = Math.round(ang / 15) * 15;
      ang = Math.round(((ang % 360) + 360) % 360);
      setLayers((ls) => ls.map((l) => (l.id === rt.id
        ? { ...l, style: { ...(l.style || {}), rotation: ang } } : l)));
      return;
    }
    if (!dragRef.current) return;
    const d = dragRef.current;
    const dx = (e.clientX - d.startX) / scale;
    const dy = (e.clientY - d.startY) / scale;
    setLayers((ls) => ls.map((l) => l.id === d.id
      ? { ...l, bbox: [Math.round(d.bbox[0] + dx), Math.round(d.bbox[1] + dy), d.bbox[2], d.bbox[3]] }
      : l));
  }, [scale]);

  const onPointerUp = () => {
    dragRef.current = null;
    resizeRef.current = null;
    rotateRef.current = null;
  };

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove]);

  // ---- Modo enfoque: tecla F oculta los paneles y deja solo el lienzo ----
  useEffect(() => {
    const onKey = (e) => {
      const el = e.target;
      const typing = el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable;
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        setFocus((v) => {
          toast.success(v ? "Modo enfoque desactivado" : "Modo enfoque: pulsa F para salir");
          return !v;
        });
      } else if (e.key === "Escape") {
        setFocus(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---- Editable background canvas engine ----
  useEffect(() => {
    if (!project || !bgCanvasRef.current) return;
    const W = project.canvas.width, H = project.canvas.height;
    const cvs = bgCanvasRef.current;
    cvs.width = W; cvs.height = H;
    setBgReady(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ctx = cvs.getContext("2d", { willReadFrequently: true });
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(img, 0, 0, W, H);
      undoRef.current = []; redoRef.current = [];
      setHistTick((t) => t + 1);
      setBgReady(true);
    };
    img.onerror = () => setBgReady(true);
    img.src = fileUrl("background.png");
    // eslint-disable-next-line
  }, [project, rev]);

  const bgCtx = () => bgCanvasRef.current?.getContext("2d", { willReadFrequently: true });

  const imgCoords = (e) => {
    const c = bgCanvasRef.current; const r = c.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - r.left) * (c.width / r.width)),
      y: Math.round((e.clientY - r.top) * (c.height / r.height)),
    };
  };

  const sampleColor = (x, y) => {
    try { const d = bgCtx().getImageData(x, y, 1, 1).data; return rgbToHex(d[0], d[1], d[2]); }
    catch { return activeColor; }
  };

  const sampleRingColor = (x, y, radius) => {
    const ctx = bgCtx(); if (!ctx) return activeColor;
    const rs = [], gs = [], bs = [];
    const R = Math.max(4, radius);
    for (let a = 0; a < 360; a += 30) {
      const px = Math.round(x + Math.cos(a * Math.PI / 180) * R);
      const py = Math.round(y + Math.sin(a * Math.PI / 180) * R);
      try { const d = ctx.getImageData(px, py, 1, 1).data; rs.push(d[0]); gs.push(d[1]); bs.push(d[2]); }
      catch (e) { console.warn('No se pudo leer el píxel del lienzo:', e); }
    }
    if (!rs.length) return activeColor;
    const med = (arr) => { const s = [...arr].sort((a, b) => a - b); return s[Math.floor(s.length / 2)]; };
    return rgbToHex(med(rs), med(gs), med(bs));
  };

  const pushHistory = () => {
    try {
      undoRef.current.push(bgCanvasRef.current.toDataURL("image/png"));
      if (undoRef.current.length > 15) undoRef.current.shift();
      redoRef.current = [];
      setHistTick((t) => t + 1);
    } catch (e) { console.warn('No se pudo guardar el paso para deshacer:', e); }
  };

  const restoreSnapshot = (dataUrl) => new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const ctx = bgCtx();
      ctx.clearRect(0, 0, bgCanvasRef.current.width, bgCanvasRef.current.height);
      ctx.drawImage(img, 0, 0);
      res();
    };
    img.src = dataUrl;
  });

  const undo = async () => {
    if (!undoRef.current.length) return;
    redoRef.current.push(bgCanvasRef.current.toDataURL("image/png"));
    await restoreSnapshot(undoRef.current.pop());
    setHistTick((t) => t + 1);
  };

  const redo = async () => {
    if (!redoRef.current.length) return;
    undoRef.current.push(bgCanvasRef.current.toDataURL("image/png"));
    await restoreSnapshot(redoRef.current.pop());
    setHistTick((t) => t + 1);
  };

  const strokeSeg = (from, to, color) => {
    const ctx = bgCtx(); if (!ctx) return;
    ctx.strokeStyle = color; ctx.fillStyle = color;
    ctx.lineWidth = brushSize; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
  };

  const addTextLayer = (x, y) => {
    const id = `text_${Date.now()}`;
    const maxOrder = layers.reduce((m, l) => Math.max(m, l.order || 0), 0);
    const size = 28;
    const nl = {
      id, type: "TEXT", name: "Texto nuevo", text: "Texto nuevo", visible: true,
      order: maxOrder + 1,
      bbox: [Math.max(0, x), Math.max(0, y - Math.round(size / 2)), 240, size],
      style: { font_class: "sans", font_size_px: size, color_hex: activeColor,
               font_weight: "normal", font_style: "normal" },
    };
    setLayers((ls) => [...ls, nl]);
    setSelected(id);
    setTool("select");
  };

  const onCanvasPointerDown = (e) => {
    if (!bgReady) return;
    const p = imgCoords(e);
    if (tool === "select") { setSelected(null); return; }
    if (tool === "text") { addTextLayer(p.x, p.y); return; }
    if (tool === "eyedropper") {
      const hex = sampleColor(p.x, p.y);
      setActiveColor(hex);
      const cur = layers.find((l) => l.id === selected);
      if (cur && cur.type === "TEXT") updateStyle(cur.id, { color_hex: hex });
      toast.success(`Color tomado: ${hex}`);
      return;
    }
    if (tool === "fill") { pushHistory(); const c = bgCtx(); c.fillStyle = activeColor; c.fillRect(0, 0, bgCanvasRef.current.width, bgCanvasRef.current.height); return; }
    if (tool === "marker" || tool === "crop") { rectStartRef.current = p; setRectPreview({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); return; }
    if (tool === "lasso") { lassoRef.current = [p]; setLassoPts([p]); setLassoPath(null); setCropRegion(null); return; }
    if (tool === "rect") { pushHistory(); rectStartRef.current = p; setRectPreview({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); return; }
    if (tool === "brush" || tool === "eraser") {
      pushHistory();
      paintingRef.current = true;
      lastPtRef.current = p;
      const color = tool === "eraser" ? sampleRingColor(p.x, p.y, brushSize) : activeColor;
      eraserColorRef.current = color;
      strokeSeg(p, p, color);
    }
  };

  const onCanvasPointerMoveWin = useCallback((e) => {
    if (lassoRef.current) {
      const p = imgCoords(e);
      const last = lassoRef.current[lassoRef.current.length - 1];
      if (Math.abs(p.x - last.x) + Math.abs(p.y - last.y) > 3) {
        lassoRef.current.push(p);
        setLassoPts([...lassoRef.current]);
      }
      return;
    }
    if (rectStartRef.current) {
      const p = imgCoords(e);
      setRectPreview({ x0: rectStartRef.current.x, y0: rectStartRef.current.y, x1: p.x, y1: p.y });
      return;
    }
    if (!paintingRef.current) return;
    const p = imgCoords(e);
    const color = tool === "eraser" ? eraserColorRef.current : activeColor;
    strokeSeg(lastPtRef.current, p, color);
    lastPtRef.current = p;
    // eslint-disable-next-line
  }, [tool, activeColor, brushSize]);

  const onCanvasPointerUpWin = useCallback(() => {
    if (lassoRef.current) {
      const pts = lassoRef.current;
      lassoRef.current = null;
      if (pts.length >= 3) {
        const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
        const bx = Math.min(...xs), by = Math.min(...ys);
        const bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;
        if (bw > 16 && bh > 16) {
          setLassoPath(pts);
          setCropRegion([bx, by, bw, bh]);
        } else {
          setLassoPts(null);
          toast.error("El contorno es demasiado pequeño.");
        }
      } else { setLassoPts(null); }
      return;
    }
    if (rectStartRef.current && rectPreview) {
      const { x0, y0, x1, y1 } = rectPreview;
      const bx = Math.min(x0, x1), by = Math.min(y0, y1);
      const bw = Math.abs(x1 - x0), bh = Math.abs(y1 - y0);
      if (tool === "marker") {
        if (bw > 4 && bh > 4) {
          setMarkerRegion([bx, by, bw, bh]);
          toast.success("Zona marcada. Escribe qué corregir y la IA se enfocará ahí.");
        }
      } else if (tool === "crop") {
        if (bw > 16 && bh > 16) setCropRegion([bx, by, bw, bh]);
        else toast.error("La zona de recorte es demasiado pequeña.");
      } else {
        const c = bgCtx();
        c.fillStyle = activeColor;
        c.fillRect(bx, by, bw, bh);
      }
      rectStartRef.current = null;
      setRectPreview(null);
    }
    paintingRef.current = false;
    lastPtRef.current = null;
    // eslint-disable-next-line
  }, [rectPreview, activeColor, tool]);

  useEffect(() => {
    window.addEventListener("pointermove", onCanvasPointerMoveWin);
    window.addEventListener("pointerup", onCanvasPointerUpWin);
    return () => {
      window.removeEventListener("pointermove", onCanvasPointerMoveWin);
      window.removeEventListener("pointerup", onCanvasPointerUpWin);
    };
  }, [onCanvasPointerMoveWin, onCanvasPointerUpWin]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { layers };
      if (bgReady && bgCanvasRef.current) {
        try { body.background_base64 = bgCanvasRef.current.toDataURL("image/png"); }
        catch (e) { console.warn('No se pudo exportar el fondo dibujado:', e); }
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/save${q}`, body, authOpts);
      setDownloads(data.downloads);
      setRev((r) => r + 1);
      toast.success(`Guardado. Se cobraron ${data.cost} créditos. Saldo: ${data.balance}`);
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail || "Créditos insuficientes");
      else toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const openDewarpPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/dewarp/preview${q}`, {}, authOpts);
      setDewarpPreview({ before: `${data.before}&v=${Date.now()}`, after: `${data.after}&v=${Date.now()}`, dewarped: data.dewarped });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar la previsualización");
    } finally { setPreviewing(false); }
  };

  const confirmDewarp = async () => {
    setDewarping(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/dewarp${q}`, {}, authOpts);
      setProject((p) => ({ ...p, canvas: data.canvas, layers: data.layers }));
      setLayers(data.layers.map((l) => ({ ...l })));
      setSelected(null);
      setDownloads(data.downloads);
      setDewarpPreview(null);
      setRev((r) => r + 1);
      toast.success(data.dewarped
        ? "Documento enderezado y proyecto regenerado."
        : "No se detectó inclinación clara; proyecto regenerado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enderezar");
    } finally { setDewarping(false); }
  };

  const registerFont = async (cssName, url) => {
    try {
      const buf = await (await fetch(url)).arrayBuffer();
      const face = new FontFace(cssName, buf);
      await face.load();
      document.fonts.add(face);
    } catch (e) {
      console.warn('No se pudo medir la tipografía, se usa la familia genérica:', e);
    }
  };

  const loadLibrary = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/editor/${jobId}/fonts/library${q}`, authOpts);
      setLibrary(data.fonts || []);
    } catch (e) { console.warn('No se pudo preparar la vista previa del texto:', e); }
  };

  const applyLibraryFont = async (libraryId) => {
    if (!libraryId || !selected) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/font/use${q}`, { library_id: libraryId }, authOpts);
      const cssName = "cf_" + data.font_file.split("/").pop().split(".")[0];
      await registerFont(cssName, data.url);
      updateStyle(selected, { font_file: data.font_file, font_family: data.family, font_css: cssName });
      toast.success(`Fuente "${data.family}" aplicada.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aplicar la fuente");
    }
  };

  const uploadFont = async (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().slice(-4);
    if (ext !== ".ttf" && ext !== ".otf") { toast.error("Sube un archivo .ttf o .otf"); return; }
    setFontUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/font${q}`, fd, authOpts);
      const cssName = "cf_" + data.font_file.split("/").pop().split(".")[0];
      await registerFont(cssName, data.url);
      if (selected) updateStyle(selected, { font_file: data.font_file, font_family: data.family, font_css: cssName });
      toast.success(`Fuente "${data.family}" cargada.`);
      loadLibrary();
      return { font_file: data.font_file, font_family: data.family, font_css: cssName };
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir la fuente");
    } finally { setFontUploading(false); }
  };

  const applyFontToAll = () => {
    const st = sel?.style || {};
    if (!st.font_file) { toast.error("Primero sube o selecciona una fuente"); return; }
    setLayers((ls) => ls.map((l) => l.type === "TEXT"
      ? { ...l, style: { ...(l.style || {}), font_file: st.font_file, font_family: st.font_family, font_css: st.font_css } }
      : l));
    toast.success("Fuente aplicada a todas las capas de texto.");
  };

  const clearCustomFont = (id) =>
    setLayers((ls) => ls.map((l) => {
      if (l.id !== id) return l;
      const s = { ...(l.style || {}) };
      delete s.font_file; delete s.font_css;
      return { ...l, style: s };
    }));

  const pollAiTask = (taskId, label) => new Promise((resolve) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/editor/${jobId}/ai/task/${taskId}${q}`, authOpts);
        if (data.status === "done") {
          clearInterval(iv);
          setDownloads(data.result.downloads);
          setRev((r) => r + 1);
          toast.success(`${label} listo con IA.`);
          resolve(true);
        } else if (data.status === "error") {
          clearInterval(iv);
          toast.error(data.error || "La IA no pudo completar la operación");
          resolve(false);
        }
      } catch (e) { console.warn('Fallo al consultar la tarea, se vuelve a intentar:', e); }
      if (tries > 90) { clearInterval(iv); toast.error("La IA tardó demasiado. Intenta de nuevo."); resolve(false); }
    }, 2000);
  });

  const aiEnhance = async () => {
    setAiBusy("enhance");
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/ai/enhance${q}`, { instruction: aiInstruction }, authOpts);
      await pollAiTask(data.task_id, "Mejora de imagen");
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail || "Créditos insuficientes");
      else toast.error(e.response?.data?.detail || "No se pudo iniciar la mejora con IA");
    } finally { setAiBusy(null); }
  };

  const aiGenerateBg = async () => {
    if (!aiPrompt.trim()) { toast.error("Escribe cómo quieres el fondo"); return; }
    setAiBusy("generate");
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/ai/generate-bg${q}`, { prompt: aiPrompt, quality }, authOpts);
      await pollAiTask(data.task_id, "Fondo generado");
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail || "Créditos insuficientes");
      else toast.error(e.response?.data?.detail || "No se pudo iniciar la generación");
    } finally { setAiBusy(null); }
  };

  const aiTextOp = async (mode) => {
    const cur = layers.find((l) => l.id === selected);
    if (!cur || cur.type !== "TEXT") { toast.error("Selecciona una capa de texto"); return; }
    setAiBusy(mode);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/ai/text${q}`,
        { provider: aiProvider, mode, text: cur.text, instruction: aiInstruction }, authOpts);
      const changed = (data.result || "") !== (cur.text || "");
      updateLayer(cur.id, { text: data.result });
      toast.success(changed
        ? `Texto actualizado con ${aiProvider === "anthropic" ? "Claude" : "Gemini"}.`
        : "La IA no encontró cambios que aplicar.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "La IA no pudo procesar el texto");
    } finally { setAiBusy(null); }
  };


  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMsgs, chatOpen]);

  const undoAiChange = () => {
    if (!aiLayerUndoRef.current) return;
    setLayers(aiLayerUndoRef.current.map((l) => ({ ...l })));
    aiLayerUndoRef.current = null;
    toast.success("Cambio de IA deshecho.");
  };

  const runAiCommand = async (text) => {
    const instruction = (text ?? aiCmd).trim();
    if (!instruction) { toast.error("Escribe qué quieres que se realice"); return; }
    if (aiCmdBusy) return;
    setAiCmdBusy(true);
    setChatOpen(true);
    setChatMsgs((m) => [...m, { id: `u-${Date.now()}-${m.length}`, role: "user", text: instruction }]);
    setAiCmd("");
    aiLayerUndoRef.current = layers.map((l) => ({ ...l }));
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/ai/command${q}`,
        { instruction, layers, selected_id: selected, region: markerRegion },
        authOpts);
      const changed = Array.isArray(data.layer_ops) && data.layer_ops.length > 0;
      if (Array.isArray(data.layers)) setLayers(data.layers.map((l) => ({ ...l })));
      if (!changed && !data.task_id) aiLayerUndoRef.current = null;
      setChatMsgs((m) => [...m, { id: `a-${Date.now()}-${m.length}`, role: "assistant", text: data.reply || "Listo.", undoable: changed }]);
      if (data.task_id) {
        setChatMsgs((m) => [...m, { id: `a-${Date.now()}-${m.length}`, role: "assistant", text: "🎨 Trabajando en la imagen con IA…" }]);
        setAiBusy("command");
        await pollAiTask(data.task_id, "Edición con IA");
        setAiBusy(null);
      }
      toast.success(data.reply || "Cambios aplicados con IA.");
    } catch (e) {
      const msg = e.response?.status === 402
        ? (e.response.data.detail || "Créditos insuficientes")
        : (e.response?.data?.detail || "La IA no pudo procesar la instrucción");
      setChatMsgs((m) => [...m, { id: `a-${Date.now()}-${m.length}`, role: "assistant", text: "⚠️ " + msg }]);
      toast.error(msg);
    } finally { setAiCmdBusy(false); }
  };


  const loadPages = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/editor/${jobId}/pages${q}`, authOpts);
      setPages(data.pages || []);
    } catch (e) { console.warn('Sin páginas adicionales (proyecto de una sola página):', e); }
  };

  const gotoPage = (pg) => {
    if (pg.current) return;
    window.location.assign(`/editor/${pg.job_id}${pg.token ? `?t=${pg.token}` : ""}`);
  };

  const movePage = async (dir) => {
    const i = pages.findIndex((p) => p.current);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= pages.length) return;
    const order = pages.map((p) => p.job_id);
    [order[i], order[j]] = [order[j], order[i]];
    setPagesBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/pages/reorder${q}`, { order }, authOpts);
      setPages(data.pages || []);
      toast.success("Páginas reordenadas.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reordenar");
    } finally { setPagesBusy(false); }
  };

  const removePage = async (pg) => {
    if (!window.confirm(`¿Eliminar la página ${pg.page} del proyecto?`)) return;
    setPagesBusy(true);
    try {
      const { data } = await axios.delete(
        `${BACKEND_URL}/api/editor/${jobId}/pages/${pg.job_id}${q}`, authOpts);
      if (pg.current) {
        const next = pages.find((p) => p.job_id === data.next_job_id);
        window.location.assign(`/editor/${data.next_job_id}${next?.token ? `?t=${next.token}` : ""}`);
        return;
      }
      toast.success(`Página ${pg.page} eliminada.`);
      loadPages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar la página");
    } finally { setPagesBusy(false); }
  };

  const exportFullPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/export-pdf${q}`, {}, authOpts);
      setDownloads((d) => ({ ...(d || {}), pdf_full: data.url }));
      toast.success(`PDF completo generado (${data.pages} página(s)).`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el PDF completo");
    } finally { setPdfBusy(false); }
  };

  const doCrop = async (ai) => {
    if (!cropRegion) return;
    setCropBusy(true);
    try {
      const body = { bbox: cropRegion, layers, ai: !!ai };
      if (lassoPath) body.polygon = lassoPath.map((p) => [p.x, p.y]);
      if (bgReady && bgCanvasRef.current) {
        try { body.background_base64 = bgCanvasRef.current.toDataURL("image/png"); }
        catch (e) { console.warn('No se pudo exportar el fondo dibujado:', e); }
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/crop${q}`, body, authOpts);
      setProject((p) => ({ ...p, canvas: data.canvas, layers: data.layers }));
      setLayers(data.layers.map((l) => ({ ...l })));
      setSelected(null);
      setCropRegion(null);
      setLassoPath(null);
      setLassoPts(null);
      setMarkerRegion(null);
      setDownloads(data.downloads);
      setRev((r) => r + 1);
      setTool("select");
      toast.success(ai ? "Recortado. La IA está reencuadrando…" : "Documento recortado.");
      if (data.ai_error) toast.error(data.ai_error);
      if (data.task_id) {
        setAiBusy("crop");
        await pollAiTask(data.task_id, "Reencuadre con IA");
        setAiBusy(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo recortar");
    } finally { setCropBusy(false); }
  };

  const setLayerFilter = (key, value) => {
    if (!selected) return;
    const cur = (layers.find((l) => l.id === selected)?.style || {}).filters || FILTER_DEFAULTS;
    updateStyle(selected, { filters: { ...FILTER_DEFAULTS, ...cur, [key]: value } });
  };

  const resetLayerFilters = () => {
    if (!selected) return;
    updateStyle(selected, { filters: { ...FILTER_DEFAULTS } });
    toast.success("Filtros de la capa restablecidos.");
  };

  const bakeBgFilters = () => {
    const c = bgCanvasRef.current;
    const f = cssFilter(bgFilters);
    if (!c || f === "none") { toast.error("Ajusta primero brillo, contraste o saturación."); return; }
    pushHistory();
    const tmp = document.createElement("canvas");
    tmp.width = c.width; tmp.height = c.height;
    tmp.getContext("2d").drawImage(c, 0, 0);
    const ctx = bgCtx();
    ctx.filter = f;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(tmp, 0, 0);
    ctx.filter = "none";
    setBgFilters({ ...FILTER_DEFAULTS });
    toast.success("Filtros aplicados al fondo. Guarda para exportarlos.");
  };

  const run3d = async () => {
    if (!obj3d.trim()) { toast.error("Describe qué quieres que salga del papel"); return; }
    setAiBusy("3d");
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/editor/${jobId}/ai/3d${q}`,
        { object: obj3d, mode: mode3d, quality }, authOpts);
      setShow3d(false);
      toast.success("La IA está creando tu diseño 3D…");
      await pollAiTask(data.task_id, "Diseño 3D");
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail || "Créditos insuficientes");
      else toast.error(e.response?.data?.detail || "No se pudo crear el diseño 3D");
    } finally { setAiBusy(null); }
  };

  const sendTelegram = async () => {
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/editor/${jobId}/send${q}`,
        { format: sendFormat }, authOpts);
      toast.success(`Enviado a Telegram (${sendFormat.toUpperCase()}).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar a Telegram");
    } finally { setSending(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center theme-noir" style={{ backgroundColor: "#050507" }}>
      <Loader2 className="w-8 h-8 animate-spin text-red-400" />
    </div>
  );
  if (!project) return (
    <div className="min-h-screen flex items-center justify-center text-slate-400 theme-noir" style={{ backgroundColor: "#050507" }}>
      Proyecto no disponible.
    </div>
  );

  const sel = layers.find((l) => l.id === selected);

  return (
    <div className="min-h-screen flex flex-col theme-noir" style={{ backgroundColor: "#050507" }}>
      {/* Header */}
      <div className={`h-16 border-b flex items-center justify-between px-6 ${focus ? "hidden" : ""}`} style={{ borderColor: "#1B1B21", backgroundColor: "#0B0B10" }}>
        <div className="flex items-center gap-3">
          {adminUser && !token && (
            <Link to="/jobs" data-testid="editor-back-btn" className="text-slate-400 hover:text-slate-200"><ArrowLeft className="w-5 h-5" /></Link>
          )}
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FF9440,#3A1703)" }}>
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm text-slate-100">Editor de Capas</div>
            <div className="text-[11px] text-slate-500 font-mono">{jobId}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Coins className="w-3.5 h-3.5 text-amber-400" /> Guardar cuesta {cost} créditos
          </span>
          <Button data-testid="editor-dewarp-btn" onClick={openDewarpPreview} disabled={previewing || dewarping || saving}
            variant="outline" className="border-[#2A2A32] bg-transparent text-slate-200 hover:bg-[#1B1B21]">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" /> Enderezar</>}
          </Button>
          <Button data-testid="editor-save-btn" onClick={save} disabled={saving || dewarping}
            className="bg-[#C2620E] hover:bg-[#FF9440] text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Guardar y re-exportar</>}
          </Button>
        </div>
      </div>

      {/* Multi-page bar */}
      {pages.length > 1 && (
        <div data-testid="pages-bar" className={`border-b flex items-center gap-2 px-4 py-2 flex-wrap ${focus ? "hidden" : ""}`}
          style={{ borderColor: "#1B1B21", backgroundColor: "#0B0B10" }}>
          <span className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
            <FileStack className="w-3.5 h-3.5 text-red-400" /> Páginas ({pages.length})
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {pages.map((pg) => (
              <div key={pg.job_id} data-testid={`page-tab-${pg.page}`}
                className={`group flex items-center gap-1.5 p-1 rounded-md border text-xs transition-colors ${pg.current ? "bg-[#C2620E] border-[#C2620E] text-white" : "border-[#1B1B21] text-slate-300 hover:bg-[#121218]"}`}>
                <button data-testid={`page-open-${pg.page}`} onClick={() => gotoPage(pg)}
                  className="flex items-center gap-1.5">
                  {pg.thumb && (
                    <img data-testid={`page-thumb-${pg.page}`} src={pg.thumb} alt={`Página ${pg.page}`}
                      className="w-7 h-9 object-cover rounded-sm border" style={{ borderColor: "#2A2A32", backgroundColor: "#fff" }} />
                  )}
                  <span className="font-mono">{pg.page}</span>
                </button>
                <button data-testid={`page-delete-${pg.page}`} onClick={() => removePage(pg)}
                  disabled={pagesBusy} title="Eliminar página"
                  className="opacity-50 hover:opacity-100 hover:text-rose-300 disabled:opacity-20">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="w-px h-5 mx-1" style={{ backgroundColor: "#1B1B21" }} />
          <button data-testid="page-move-left" onClick={() => movePage(-1)} disabled={pagesBusy}
            title="Mover esta página antes"
            className="p-1 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#121218] disabled:opacity-30">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="page-move-right" onClick={() => movePage(1)} disabled={pagesBusy}
            title="Mover esta página después"
            className="p-1 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#121218] disabled:opacity-30">
            <ChevronRight className="w-4 h-4" />
          </button>
          <Button data-testid="export-full-pdf-btn" onClick={exportFullPdf} disabled={pdfBusy}
            variant="outline" className="ml-auto h-8 border-[#2A2A32] bg-transparent text-slate-200 hover:bg-[#1B1B21] text-xs">
            {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileText className="w-3.5 h-3.5 mr-1" /> Exportar PDF completo</>}
          </Button>
        </div>
      )}

      {/* Tools toolbar */}
      <div data-testid="editor-toolbar" className="border-b flex items-center gap-1.5 px-4 py-2 flex-wrap" style={{ borderColor: "#1B1B21", backgroundColor: "#08080C" }}>
        {[["select", MousePointer2, "Seleccionar"], ["text", Type, "Agregar texto"], ["crop", Crop, "Recortar zona"], ["lasso", Lasso, "Lazo a mano alzada"], ["marker", Highlighter, "Marcar zona (IA)"], ["eyedropper", Pipette, "Igualar color"], ["brush", Brush, "Pincel de fondo"], ["eraser", Eraser, "Borrador inteligente"], ["rect", Square, "Rellenar zona"], ["fill", PaintBucket, "Rellenar todo"]].map(([key, Icon, label]) => (
          <button key={key} data-testid={`tool-${key}`} title={label} onClick={() => setTool(key)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors ${tool === key ? "bg-[#C2620E] border-[#C2620E] text-white" : "border-[#1B1B21] text-slate-300 hover:bg-[#121218]"}`}>
            <Icon className="w-4 h-4" /> <span className="hidden lg:inline">{label}</span>
          </button>
        ))}

        <div className="w-px h-6 mx-1" style={{ backgroundColor: "#1B1B21" }} />

        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          Color
          <input data-testid="bg-color-picker" type="color" value={activeColor}
            onChange={(e) => setActiveColor(e.target.value)}
            className="w-8 h-7 bg-transparent border border-[#1B1B21] rounded cursor-pointer" />
          <span className="font-mono text-[11px] text-slate-500">{activeColor}</span>
        </label>

        {(tool === "brush" || tool === "eraser") && (
          <label className="flex items-center gap-2 text-xs text-slate-400 ml-2">
            Grosor
            <input data-testid="brush-size-slider" type="range" min={2} max={80} value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value, 10))} className="w-28 accent-[#C2620E]" />
            <span className="font-mono text-[11px] text-slate-500 w-8">{brushSize}px</span>
          </label>
        )}

        <div className="w-px h-6 mx-1" style={{ backgroundColor: "#1B1B21" }} />

        <button data-testid="undo-btn" onClick={undo} disabled={!undoRef.current.length} title="Deshacer"
          className="p-1.5 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#121218] disabled:opacity-30">
          <Undo2 className="w-4 h-4" />
        </button>
        <button data-testid="redo-btn" onClick={redo} disabled={!redoRef.current.length} title="Rehacer"
          className="p-1.5 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#121218] disabled:opacity-30">
          <Redo2 className="w-4 h-4" />
        </button>

        {tool === "crop" && (
          <span className="text-[11px] text-red-300 ml-2">
            Arrastra sobre el documento para elegir la zona a recortar.
          </span>
        )}
        {tool === "lasso" && (
          <span className="text-[11px] text-red-300 ml-2">
            Dibuja el contorno a mano alzada; al soltar te propongo el recorte.
          </span>
        )}

        <button data-testid="focus-mode-btn" onClick={() => setFocus((v) => !v)}
          title={focus ? "Salir del modo enfoque (F)" : "Modo enfoque: solo el lienzo (F)"}
          className="p-1.5 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#121218]">
          {focus ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>

        <button data-testid="tool-3d" onClick={() => setShow3d(true)} disabled={!!aiBusy}
          title="Diseño 3D que sale del papel"
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-white disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#3A1E05,#FF9440)" }}>
          {aiBusy === "3d" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Box className="w-4 h-4" />} 3D
        </button>
      </div>

      {/* Prominent AI command bar */}
      <div data-testid="ai-command-bar" className={`border-b px-4 py-3 ${focus ? "hidden" : ""}`} style={{ borderColor: "#1B1B21", background: "rgba(18,18,24,0.82)" }}>
        <div className="flex items-center gap-3 max-w-5xl mx-auto">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#2A1403,#FF9440)" }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <input data-testid="ai-command-input" value={aiCmd}
              onChange={(e) => setAiCmd(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !aiCmdBusy) runAiCommand(); }}
              placeholder="Escríbeme lo que quieres que se realice y aquí mismo te lo preparo…"
              className="w-full bg-[#08080C] border border-[#2A2A32] focus:border-[#FF9440] outline-none text-slate-100 text-sm rounded-lg px-3 py-2.5 transition-colors" />
          </div>
          {markerRegion && (
            <button data-testid="ai-clear-marker" onClick={() => setMarkerRegion(null)}
              title="Quitar zona marcada"
              className="flex items-center gap-1 text-xs text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-md px-2 py-2 hover:bg-amber-500/20">
              <Highlighter className="w-3.5 h-3.5" /> Zona marcada <X className="w-3 h-3" />
            </button>
          )}
          <Button data-testid="ai-command-run" onClick={() => runAiCommand()} disabled={aiCmdBusy || !aiCmd.trim()}
          className="bg-gradient-to-r from-[#8A3C06] to-[#FF9440] hover:from-[#B4560E] hover:to-[#FFA85C] text-white shrink-0">
            {aiCmdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" /> Preparar</>}
          </Button>
          <VoiceButton testId="ai-voice-btn" className="w-10 h-10 shrink-0" title="Dímelo por voz"
            url={`${BACKEND_URL}/api/editor/${jobId}/voice${q}`}
            onText={(t) => runAiCommand(t)} />
          <button data-testid="ai-chat-toggle" onClick={() => setChatOpen((o) => !o)}
            title="Asistente"
            className="w-11 h-11 rounded-full flex items-center justify-center shrink-0">
            <AiAvatar size={42} busy={aiCmdBusy} />
          </button>
        </div>
        <div className="text-[11px] text-slate-500 mt-1.5 max-w-5xl mx-auto">
          Ejemplos: “quítale el fondo”, “pon el título en azul y más grande”, “corrige la ortografía del texto”, “traduce todo al inglés”. También puedes pulsar el micrófono y decírmelo hablando.
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Layer panel */}
        <div data-testid="editor-side-panel" className={`w-72 border-r overflow-y-auto ${focus ? "hidden" : ""}`} style={{ borderColor: "#1B1B21", backgroundColor: "#0B0B10" }}>
          <div className="p-4 text-xs text-slate-500 uppercase tracking-wide">Capas ({layers.length})</div>
          {layersTopDown.map((l) => (
            <div key={l.id} data-testid={`layer-row-${l.id}`}
              onClick={() => setSelected(l.id)}
              className={`flex items-center gap-2 px-4 py-2.5 cursor-pointer border-l-2 transition-colors ${selected === l.id ? "bg-[#1C1408]" : "hover:bg-[#0E0E13]"}`}
              style={{ borderColor: selected === l.id ? (TYPE_COLOR[l.type] || "#C2620E") : "transparent" }}>
              <button data-testid={`layer-visibility-${l.id}`} onClick={(e) => { e.stopPropagation(); updateLayer(l.id, { visible: !l.visible }); }}
                className="text-slate-400 hover:text-slate-100">
                {l.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-slate-600" />}
              </button>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: TYPE_COLOR[l.type] || "#94A3B8", backgroundColor: `${TYPE_COLOR[l.type] || "#94A3B8"}18` }}>{l.type}</span>
              <span className={`text-sm truncate flex-1 ${l.visible ? "text-slate-300" : "text-slate-600"}`}>{l.name}</span>
              {l.type !== "BACKGROUND" && (
                <button data-testid={`layer-delete-${l.id}`} onClick={(e) => { e.stopPropagation(); setLayers((ls) => ls.filter((x) => x.id !== l.id)); if (selected === l.id) setSelected(null); }}
                  className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}

          {sel && (
            <div className="p-4 border-t mt-2" style={{ borderColor: "#1B1B21" }}>
              <div className="text-xs text-slate-500 uppercase mb-2">Editar capa</div>
              <label className="text-[11px] text-slate-500">Nombre</label>
              <Input data-testid="layer-name-input" value={sel.name}
                onChange={(e) => updateLayer(sel.id, { name: e.target.value })}
                className="bg-[#0B0B10] border-[#1B1B21] text-slate-100 mb-3 text-sm" />

              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] text-slate-500 w-16">Opacidad</span>
                <input data-testid="layer-opacity" type="range" min={0} max={100}
                  value={(sel.style || {}).opacity ?? 100}
                  onChange={(e) => updateStyle(sel.id, { opacity: parseInt(e.target.value, 10) })}
                  className="flex-1 accent-[#C2620E]" />
                <span className="text-[11px] text-slate-500 w-9 text-right">{(sel.style || {}).opacity ?? 100}%</span>
              </div>

              {sel.type !== "BACKGROUND" && (
                <div className="mb-3 p-2 rounded-md border" style={{ borderColor: "#1B1B21", backgroundColor: "#08080C" }}>
                  <div className="text-[11px] text-slate-500 mb-1.5 flex items-center gap-1">
                    <Maximize2 className="w-3 h-3 text-red-400" /> Transformar
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-500">Ancho</label>
                      <Input data-testid="layer-width-input" type="number" min={8} value={sel.bbox[2]}
                        onChange={(e) => updateLayer(sel.id, { bbox: [sel.bbox[0], sel.bbox[1], Math.max(8, parseInt(e.target.value || "8", 10)), sel.bbox[3]] })}
                        className="bg-[#0B0B10] border-[#1B1B21] text-slate-100 text-xs h-8" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-500">Alto</label>
                      <Input data-testid="layer-height-input" type="number" min={8} value={sel.bbox[3]}
                        onChange={(e) => {
                          const nh = Math.max(8, parseInt(e.target.value || "8", 10));
                          const old = sel.bbox[3] || nh;
                          const patch = { bbox: [sel.bbox[0], sel.bbox[1], sel.bbox[2], nh] };
                          if (sel.type === "TEXT") {
                            const fs = (sel.style || {}).font_size_px || old;
                            patch.style = { ...(sel.style || {}), font_size_px: Math.max(6, Math.round(fs * (nh / old))) };
                          }
                          updateLayer(sel.id, patch);
                        }}
                        className="bg-[#0B0B10] border-[#1B1B21] text-slate-100 text-xs h-8" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1 w-14"><RotateCw className="w-3 h-3" /> Ángulo</span>
                    <input data-testid="layer-rotation-slider" type="range" min={0} max={359}
                      value={(sel.style || {}).rotation ?? 0}
                      onChange={(e) => updateStyle(sel.id, { rotation: parseInt(e.target.value, 10) })}
                      className="flex-1 accent-amber-500" />
                    <Input data-testid="layer-rotation-input" type="number" min={0} max={359}
                      value={(sel.style || {}).rotation ?? 0}
                      onChange={(e) => updateStyle(sel.id, { rotation: ((parseInt(e.target.value || "0", 10) % 360) + 360) % 360 })}
                      className="bg-[#0B0B10] border-[#1B1B21] text-slate-100 text-xs h-8 w-16" />
                  </div>
                  <button data-testid="layer-rotation-reset" onClick={resetTransform}
                    className="w-full mt-2 py-1 rounded-md border border-[#1B1B21] text-[11px] text-slate-300 hover:bg-[#0E0E13]">
                    Restablecer rotación
                  </button>
                </div>
              )}

              {sel.type !== "BACKGROUND" && (
                <div className="mb-3 p-2 rounded-md border" style={{ borderColor: "#1B1B21", backgroundColor: "#08080C" }}>
                  <div className="text-[11px] text-slate-500 mb-1.5 flex items-center gap-1">
                    <SlidersHorizontal className="w-3 h-3 text-emerald-400" /> Filtros de la capa
                  </div>
                  {[["brightness", "Brillo"], ["contrast", "Contraste"], ["saturation", "Saturación"]].map(([key, label]) => {
                    const val = ((sel.style || {}).filters || FILTER_DEFAULTS)[key] ?? 100;
                    return (
                      <div key={key} className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-slate-500 w-16">{label}</span>
                        <input data-testid={`layer-filter-${key}`} type="range" min={0} max={200} value={val}
                          onChange={(e) => setLayerFilter(key, parseInt(e.target.value, 10))}
                          className="flex-1 accent-[#FF9440]" />
                        <span className="text-[10px] text-slate-500 w-8 text-right">{val}%</span>
                      </div>
                    );
                  })}
                  <button data-testid="layer-filters-reset" onClick={resetLayerFilters}
                    className="w-full mt-1 py-1 rounded-md border border-[#1B1B21] text-[11px] text-slate-300 hover:bg-[#0E0E13]">
                    Restablecer filtros
                  </button>
                </div>
              )}

              <div className="grid grid-cols-4 gap-1.5 mb-3">
                <button data-testid="layer-forward" title="Traer al frente" onClick={() => bringForward(sel.id)}                  className="flex items-center justify-center py-1.5 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#0E0E13]"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid="layer-backward" title="Enviar atrás" onClick={() => sendBackward(sel.id)}
                  className="flex items-center justify-center py-1.5 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#0E0E13]"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid="layer-duplicate" title="Duplicar" onClick={() => duplicateLayer(sel.id)}
                  className="flex items-center justify-center py-1.5 rounded-md border border-[#1B1B21] text-slate-300 hover:bg-[#0E0E13]"><Copy className="w-4 h-4" /></button>
                <button data-testid="layer-lock" title={sel.locked ? "Desbloquear" : "Bloquear"} onClick={() => toggleLock(sel.id)}
                  className={`flex items-center justify-center py-1.5 rounded-md border ${sel.locked ? "bg-amber-500/20 border-amber-500 text-amber-400" : "border-[#1B1B21] text-slate-300 hover:bg-[#0E0E13]"}`}>{sel.locked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}</button>
              </div>

              {sel.type === "TEXT" && (
                <>
                  <label className="text-[11px] text-slate-500">Texto</label>
                  <Input data-testid="layer-text-input" value={sel.text || ""}
                    onChange={(e) => updateLayer(sel.id, { text: e.target.value })}
                    className="bg-[#0B0B10] border-[#1B1B21] text-slate-100 text-sm" />

                  <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
                    <Type className="w-3 h-3" /> Fuente
                  </div>
                  <select data-testid="layer-font-family"
                    value={(sel.style || {}).font_class || "sans"}
                    onChange={(e) => updateStyle(sel.id, { font_class: e.target.value })}
                    className="w-full bg-[#0B0B10] border border-[#1B1B21] text-slate-100 text-sm rounded-md px-2 py-1.5 mt-1">
                    <option value="sans">Sans</option>
                    <option value="serif">Serif</option>
                    <option value="mono">Mono</option>
                  </select>

                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1">
                      <label className="text-[11px] text-slate-500">Tamaño (px)</label>
                      <Input data-testid="layer-font-size" type="number" min={6}
                        value={(sel.style || {}).font_size_px || sel.bbox[3]}
                        onChange={(e) => updateStyle(sel.id, { font_size_px: Math.max(6, parseInt(e.target.value || "0", 10)) })}
                        className="bg-[#0B0B10] border-[#1B1B21] text-slate-100 text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500">Color</label>
                      <input data-testid="layer-font-color" type="color"
                        value={(sel.style || {}).color_hex || "#111111"}
                        onChange={(e) => updateStyle(sel.id, { color_hex: e.target.value })}
                        className="block w-10 h-9 mt-0.5 bg-transparent border border-[#1B1B21] rounded-md cursor-pointer" />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <button data-testid="layer-font-bold"
                      onClick={() => updateStyle(sel.id, { font_weight: (sel.style || {}).font_weight === "bold" ? "normal" : "bold" })}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md border text-sm ${(sel.style || {}).font_weight === "bold" ? "bg-[#C2620E] border-[#C2620E] text-white" : "border-[#1B1B21] text-slate-300"}`}>
                      <Bold className="w-3.5 h-3.5" /> Negrita
                    </button>
                    <button data-testid="layer-font-italic"
                      onClick={() => updateStyle(sel.id, { font_style: (sel.style || {}).font_style === "italic" ? "normal" : "italic" })}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md border text-sm ${(sel.style || {}).font_style === "italic" ? "bg-[#C2620E] border-[#C2620E] text-white" : "border-[#1B1B21] text-slate-300"}`}>
                      <Italic className="w-3.5 h-3.5" /> Cursiva
                    </button>
                  </div>

                  <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
                    <UploadCloud className="w-3 h-3" /> Fuente propia (.ttf/.otf)
                  </div>
                  {library.length > 0 && (
                    <select data-testid="font-library-select" defaultValue=""
                      onChange={(e) => { applyLibraryFont(e.target.value); e.target.value = ""; }}
                      className="w-full bg-[#0B0B10] border border-[#1B1B21] text-slate-100 text-sm rounded-md px-2 py-1.5 mt-1">
                      <option value="" disabled>Mis fuentes ({library.length})…</option>
                      {library.map((f) => (
                        <option key={f.library_id} value={f.library_id}>{f.family}</option>
                      ))}
                    </select>
                  )}
                  {(sel.style || {}).font_file ? (
                    <div className="flex items-center justify-between gap-2 mt-1 px-2 py-1.5 rounded-md border border-[#1B1B21] bg-[#0B0B10]">
                      <span data-testid="custom-font-name" className="text-xs text-emerald-400 truncate">{(sel.style || {}).font_family || "Personalizada"}</span>
                      <button data-testid="custom-font-clear" onClick={() => clearCustomFont(sel.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <label data-testid="custom-font-upload-label"
                      className="mt-1 flex items-center justify-center gap-2 py-1.5 rounded-md border border-dashed border-[#2A2A32] text-sm text-slate-300 cursor-pointer hover:bg-[#0E0E13]">
                      {fontUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UploadCloud className="w-4 h-4" /> Subir .ttf</>}
                      <input data-testid="custom-font-input" type="file" accept=".ttf,.otf" className="hidden"
                        onChange={(e) => uploadFont(e.target.files?.[0])} />
                    </label>
                  )}
                  <button data-testid="custom-font-apply-all" onClick={applyFontToAll}
                    disabled={!(sel.style || {}).font_file}
                    className="w-full mt-2 py-1.5 rounded-md border border-[#1B1B21] text-xs text-slate-300 hover:bg-[#0E0E13] disabled:opacity-40">
                    Aplicar a todas las capas de texto
                  </button>

                  <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-[#F0B176]" /> IA de texto
                  </div>
                  <select data-testid="ai-text-provider" value={aiProvider}
                    onChange={(e) => setAiProvider(e.target.value)}
                    className="w-full bg-[#0B0B10] border border-[#1B1B21] text-slate-100 text-sm rounded-md px-2 py-1.5 mt-1">
                    <option value="gemini">Gemini</option>
                    <option value="anthropic">Claude</option>
                  </select>
                  <div className="grid grid-cols-3 gap-1.5 mt-2">
                    <button data-testid="ai-text-fix" onClick={() => aiTextOp("fix")} disabled={!!aiBusy}
                      className="flex items-center justify-center gap-1 py-1.5 rounded-md border border-[#1B1B21] text-xs text-slate-200 hover:bg-[#0E0E13] disabled:opacity-40">
                      {aiBusy === "fix" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Corregir"}
                    </button>
                    <button data-testid="ai-text-improve" onClick={() => aiTextOp("improve")} disabled={!!aiBusy}
                      className="flex items-center justify-center gap-1 py-1.5 rounded-md border border-[#1B1B21] text-xs text-slate-200 hover:bg-[#0E0E13] disabled:opacity-40">
                      {aiBusy === "improve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Mejorar"}
                    </button>
                    <button data-testid="ai-text-translate" onClick={() => aiTextOp("translate")} disabled={!!aiBusy}
                      className="flex items-center justify-center gap-1 py-1.5 rounded-md border border-[#1B1B21] text-xs text-slate-200 hover:bg-[#0E0E13] disabled:opacity-40">
                      {aiBusy === "translate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Languages className="w-3 h-3" /></>}
                    </button>
                  </div>
                </>
              )}
              <div className="text-[11px] text-slate-500 mt-3 font-mono">pos: {sel.bbox[0]}, {sel.bbox[1]}</div>
            </div>
          )}

          <div className="p-4 border-t" style={{ borderColor: "#1B1B21" }}>
            <div className="text-xs uppercase mb-2 flex items-center gap-1.5 text-emerald-400">
              <Sun className="w-3.5 h-3.5" /> Ajustes del fondo
            </div>
            {[["brightness", "Brillo"], ["contrast", "Contraste"], ["saturation", "Saturación"]].map(([key, label]) => (
              <div key={key} className="flex items-center gap-2 mb-1">
                <span className="text-[10px] text-slate-500 w-16">{label}</span>
                <input data-testid={`bg-filter-${key}`} type="range" min={0} max={200} value={bgFilters[key]}
                  onChange={(e) => setBgFilters((f) => ({ ...f, [key]: parseInt(e.target.value, 10) }))}
                  className="flex-1 accent-[#FF9440]" />
                <span className="text-[10px] text-slate-500 w-8 text-right">{bgFilters[key]}%</span>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-2">
              <button data-testid="bg-filters-apply" onClick={bakeBgFilters}
                className="flex-1 py-1.5 rounded-md bg-[#1F6F4A] hover:bg-[#248057] text-white text-xs">
                Aplicar al fondo
              </button>
              <button data-testid="bg-filters-reset" onClick={() => setBgFilters({ ...FILTER_DEFAULTS })}
                className="py-1.5 px-2 rounded-md border border-[#1B1B21] text-xs text-slate-300 hover:bg-[#0E0E13]">
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[10px] text-slate-600 mt-1.5">
              La vista es en vivo; "Aplicar al fondo" fija los cambios (puedes deshacer).
            </div>
          </div>

          <div className="p-4 border-t" style={{ borderColor: "#1B1B21" }}>
            <div className="text-xs uppercase mb-2 flex items-center gap-1.5 text-[#F0B176]">
              <Sparkles className="w-3.5 h-3.5" /> IA de imagen (Nano Banana)
            </div>
            <textarea data-testid="ai-instruction" value={aiInstruction} rows={2}
              onChange={(e) => setAiInstruction(e.target.value)}
              placeholder="Instrucción opcional para mejorar (ej. quita manchas, más nitidez)…"
              className="w-full bg-[#0B0B10] border border-[#1B1B21] text-slate-100 text-xs rounded-md px-2 py-1.5 resize-none" />
            <button data-testid="ai-enhance-btn" onClick={aiEnhance} disabled={!!aiBusy}
              className="w-full mt-2 flex items-center justify-center gap-2 py-2 rounded-md bg-gradient-to-r from-[#8A3C06] to-[#FF9440] hover:from-[#B4560E] hover:to-[#FFA85C] text-white text-sm disabled:opacity-50">
              {aiBusy === "enhance" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4" /> Mejorar / limpiar con IA</>}
            </button>

            <input data-testid="ai-generate-prompt" value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="…o describe un fondo nuevo para generar"
              className="w-full mt-3 bg-[#0B0B10] border border-[#1B1B21] text-slate-100 text-xs rounded-md px-2 py-1.5" />
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-[10px] text-slate-500">Calidad</span>
              {[["1k", "1K"], ["2k", "2K"], ["4k", "4K"]].map(([v, l]) => (
                <button key={v} data-testid={`quality-${v}`} onClick={() => setQuality(v)}
                  className={`flex-1 py-1 rounded-md border text-[11px] ${quality === v ? "bg-[#C2620E] border-[#C2620E] text-white" : "border-[#1B1B21] text-slate-300 hover:bg-[#0E0E13]"}`}>
                  {l}
                </button>
              ))}
            </div>
            <button data-testid="ai-generate-btn" onClick={aiGenerateBg} disabled={!!aiBusy}
              className="w-full mt-2 flex items-center justify-center gap-2 py-2 rounded-md border border-[#FF9440]/50 text-[#F5C79A] hover:bg-[#FF9440]/10 text-sm disabled:opacity-50">
              {aiBusy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Generar fondo con IA</>}
            </button>
          </div>


          {downloads && (
            <div className="p-4 border-t" style={{ borderColor: "#1B1B21" }}>
              <div className="text-xs text-slate-500 uppercase mb-2">Descargas</div>
              {Object.entries(downloads).map(([k, url]) => url && (
                <a key={k} data-testid={`editor-download-${k}`} href={url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 py-1">
                  <Download className="w-3.5 h-3.5" /> {k.toUpperCase()}
                </a>
              ))}
            </div>
          )}

          <div className="p-4 border-t" style={{ borderColor: "#1B1B21" }}>
            <div className="text-xs text-slate-500 uppercase mb-2 flex items-center gap-1">
              <Send className="w-3.5 h-3.5" /> Reenviar por Telegram
            </div>
            <div className="flex items-center gap-2">
              <select data-testid="send-format-select" value={sendFormat}
                onChange={(e) => setSendFormat(e.target.value)}
                className="flex-1 bg-[#0B0B10] border border-[#1B1B21] text-slate-100 text-sm rounded-md px-2 py-1.5">
                {SEND_FORMATS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
              <Button data-testid="send-telegram-btn" onClick={sendTelegram} disabled={sending}
                className="bg-[#C2620E] hover:bg-[#FF9440] text-white">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <div className="text-[11px] text-slate-600 mt-2">
              Envío gratuito al chat de Telegram del proyecto.
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto flex items-start justify-center p-8 noir-canvas-grid relative">
          {focus && (
            <button data-testid="focus-exit-btn" onClick={() => setFocus(false)}
              className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] text-slate-200 border border-[#2A2A32]"
              style={{ background: "rgba(12,12,16,0.88)", backdropFilter: "blur(14px)" }}>
              <Minimize2 className="w-3.5 h-3.5" /> Salir del enfoque (F)
            </button>
          )}
          <div ref={canvasRef} data-testid="editor-canvas" className="relative shadow-2xl"
            style={{ width: dispW, height: dispH, backgroundColor: "#fff" }}>
            <canvas ref={bgCanvasRef} data-testid="bg-canvas"
              onPointerDown={onCanvasPointerDown}
              style={{ position: "absolute", left: 0, top: 0, width: dispW, height: dispH,
                touchAction: "none", filter: cssFilter(bgFilters),
                cursor: canvasCursor(tool) }} />
            {rectPreview && (
              <div style={{ position: "absolute", pointerEvents: "none",
                border: previewStroke(tool),
                background: previewFill(tool, activeColor),
                left: Math.min(rectPreview.x0, rectPreview.x1) * scale,
                top: Math.min(rectPreview.y0, rectPreview.y1) * scale,
                width: Math.abs(rectPreview.x1 - rectPreview.x0) * scale,
                height: Math.abs(rectPreview.y1 - rectPreview.y0) * scale }} />
            )}
            {markerRegion && (
              <div data-testid="marker-region-overlay" style={{ position: "absolute", pointerEvents: "none",
                border: "2px solid #F59E0B", background: "rgba(245,158,11,0.18)",
                left: markerRegion[0] * scale, top: markerRegion[1] * scale,
                width: markerRegion[2] * scale, height: markerRegion[3] * scale }}>
                <span className="absolute -top-5 left-0 text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500 text-black flex items-center gap-1">
                  <Highlighter className="w-3 h-3" /> Corregir aquí
                </span>
              </div>
            )}
            {layersBottomUp.map((l) => {
              if (!l.visible || l.type === "BACKGROUND") return null;
              const [x, y, w, h] = l.bbox;
              const interactive = tool === "select" && !l.locked;
              const common = {
                position: "absolute", left: x * scale, top: y * scale,
                width: w * scale, height: h * scale,
                opacity: ((l.style || {}).opacity ?? 100) / 100,
                transform: ((l.style || {}).rotation ? `rotate(${(l.style || {}).rotation}deg)` : "none"),
                transformOrigin: "center center",
                filter: cssFilter((l.style || {}).filters),
                outline: selected === l.id ? `2px solid ${TYPE_COLOR[l.type] || "#C2620E"}` : "none",
                cursor: interactive ? "move" : "inherit",
                pointerEvents: (tool === "select") ? "auto" : "none",
              };
              if ((l.type === "IMAGE" || l.type === "SHAPE") && l.src) {
                return <img key={l.id} src={fileUrl(l.src)} alt={l.name} draggable={false}
                  onPointerDown={(e) => onPointerDown(e, l)} style={{ ...common, userSelect: "none" }} />;
              }
              if (l.type === "TEXT") {
                const st = l.style || {};
                return (
                  <div key={l.id} onPointerDown={(e) => onPointerDown(e, l)}
                    style={{ ...common, color: st.color_hex || "#000",
                      fontSize: (st.font_size_px || h) * scale,
                      fontFamily: st.font_css ? `'${st.font_css}', ${FONT_CLASS_CSS[st.font_class] || FONT_CLASS_CSS.sans}` : (FONT_CLASS_CSS[st.font_class] || FONT_CLASS_CSS.sans),
                      fontWeight: st.font_weight === "bold" ? 700 : 400,
                      fontStyle: st.font_style === "italic" ? "italic" : "normal",
                      lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden" }}>
                    {l.text}
                  </div>
                );
              }
              return null;
            })}

            {sel && tool === "select" && sel.type !== "BACKGROUND" && !sel.locked && (
              <div data-testid="transform-box" style={{ position: "absolute",
                left: sel.bbox[0] * scale, top: sel.bbox[1] * scale,
                width: sel.bbox[2] * scale, height: sel.bbox[3] * scale,
                transform: (sel.style || {}).rotation ? `rotate(${(sel.style || {}).rotation}deg)` : "none",
                transformOrigin: "center center",
                border: "1px dashed #FF9440", pointerEvents: "none" }}>
                {HANDLES.map(([d, fx, fy, cur]) => (
                  <div key={d} data-testid={`handle-${d}`} onPointerDown={(e) => startResize(e, sel, d)}
                    style={{ position: "absolute", left: `calc(${fx * 100}% - 5px)`, top: `calc(${fy * 100}% - 5px)`,
                      width: 10, height: 10, backgroundColor: "#FF9440", border: "1px solid #fff",
                      borderRadius: 2, pointerEvents: "auto", cursor: cur, touchAction: "none" }} />
                ))}
                <div style={{ position: "absolute", left: "50%", top: -16, width: 1, height: 16, backgroundColor: "#F59E0B" }} />
                <div data-testid="handle-rotate" onPointerDown={(e) => startRotate(e, sel)}
                  style={{ position: "absolute", left: "calc(50% - 7px)", top: -30, width: 14, height: 14,
                    borderRadius: "50%", backgroundColor: "#F59E0B", border: "2px solid #fff",
                    pointerEvents: "auto", cursor: "grab", touchAction: "none" }} />
              </div>
            )}

            {lassoPts && lassoPts.length > 1 && (
              <svg data-testid="lasso-overlay" style={{ position: "absolute", left: 0, top: 0, width: dispW, height: dispH, pointerEvents: "none" }}>
                <polygon points={lassoPts.map((p) => `${p.x * scale},${p.y * scale}`).join(" ")}
                  fill="rgba(34,211,238,0.15)" stroke="#FF9440" strokeWidth="2" strokeDasharray="6 4" />
              </svg>
            )}

            {cropRegion && (
              <div data-testid="crop-region-overlay" style={{ position: "absolute", pointerEvents: "none",
                border: "2px solid #FF9440", background: "rgba(34,211,238,0.12)",
                left: cropRegion[0] * scale, top: cropRegion[1] * scale,
                width: cropRegion[2] * scale, height: cropRegion[3] * scale }}>
                <span className="absolute -top-5 left-0 text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-400 text-black flex items-center gap-1">
                  <Crop className="w-3 h-3" /> {cropRegion[2]} × {cropRegion[3]}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <Design3dModal open={show3d} onClose={() => setShow3d(false)} busy={aiBusy === "3d"}
        object={obj3d} onObjectChange={setObj3d} mode={mode3d} onModeChange={setMode3d}
        quality={quality} onQualityChange={setQuality} onRun={run3d} />

      {/* Crop confirm bar */}
      {cropRegion && (
        <div data-testid="crop-bar" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-2xl"
          style={{ borderColor: "#FF944055", backgroundColor: "#0B0B10" }}>
          <Crop className="w-4 h-4 text-red-400" />
          <span className="text-xs text-slate-300">
            {lassoPath ? "Contorno a mano alzada" : "Zona seleccionada"} <span className="font-mono text-red-300">{cropRegion[2]}×{cropRegion[3]}</span>
          </span>
          <Button data-testid="crop-apply-btn" onClick={() => doCrop(false)} disabled={cropBusy}
            className="h-8 bg-[#C2620E] hover:bg-[#FF9440] text-white text-xs">
            {cropBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Crop className="w-3.5 h-3.5 mr-1" /> {lassoPath ? "Recortar al contorno" : "Recortar aquí"}</>}
          </Button>
          <Button data-testid="crop-ai-btn" onClick={() => doCrop(true)} disabled={cropBusy}
            className="h-8 bg-gradient-to-r from-[#8A3C06] to-[#FF9440] hover:from-[#B4560E] hover:to-[#FFA85C] text-white text-xs">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Recortar y reencuadrar con IA
          </Button>
          <button data-testid="crop-cancel-btn" onClick={() => { setCropRegion(null); setLassoPath(null); setLassoPts(null); }} disabled={cropBusy}
            className="text-slate-400 hover:text-slate-200 disabled:opacity-40"><X className="w-4 h-4" /></button>
        </div>
      )}

      <AiChatPanel open={chatOpen} onClose={() => setChatOpen(false)}
        msgs={chatMsgs} busy={aiCmdBusy} chatEndRef={chatEndRef}
        canUndo={!!aiLayerUndoRef.current} onUndo={undoAiChange}
        value={aiCmd} onChange={setAiCmd} onSend={runAiCommand}
        markerRegion={markerRegion} onClearMarker={() => setMarkerRegion(null)}
        jobId={jobId} query={q} />

      {dewarpPreview && (
        <div data-testid="dewarp-modal" className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ backgroundColor: "rgba(0,0,0,0.75)" }} onClick={() => !dewarping && setDewarpPreview(null)}>
          <div className="rounded-xl border max-w-5xl w-full" style={{ borderColor: "#1B1B21", backgroundColor: "#0B0B10" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "#1B1B21" }}>
              <div className="font-heading font-bold text-sm text-slate-100 flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-red-400" /> Vista previa de enderezado
              </div>
              <button data-testid="dewarp-modal-close" onClick={() => !dewarping && setDewarpPreview(null)} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {!dewarpPreview.dewarped && (
                <div className="mb-3 text-xs text-amber-400">
                  No se detectó una inclinación clara. Puedes reprocesar igualmente o cancelar.
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] text-slate-500 uppercase mb-2">Original</div>
                  <img data-testid="dewarp-before-img" src={dewarpPreview.before} alt="antes"
                    className="w-full rounded-md border" style={{ borderColor: "#1B1B21" }} />
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 uppercase mb-2">Enderezada</div>
                  <img data-testid="dewarp-after-img" src={dewarpPreview.after} alt="después"
                    className="w-full rounded-md border" style={{ borderColor: "#C2620E" }} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t" style={{ borderColor: "#1B1B21" }}>
              <Button data-testid="dewarp-cancel-btn" onClick={() => setDewarpPreview(null)} disabled={dewarping}
                variant="outline" className="border-[#2A2A32] bg-transparent text-slate-200 hover:bg-[#1B1B21]">Cancelar</Button>
              <Button data-testid="dewarp-confirm-btn" onClick={confirmDewarp} disabled={dewarping}
                className="bg-[#C2620E] hover:bg-[#FF9440] text-white">
                {dewarping ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                Confirmar y reprocesar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
